import React from 'react';
import {
  Drawer, List, ListItem, ListItemButton, ListItemIcon,
  ListItemText, Toolbar, Divider, Box, Typography
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  AccountTree as PipelineIcon,
  Settings as SettingsIcon,
  Security as SecurityIcon,
  Share as MetaIcon,
  CheckCircle as TaskIcon,
  Chat as CommIcon,
  AutoFixHigh as WorkflowIcon,
  AdminPanelSettings as SuperAdminIcon,
  WhatsApp as WhatsAppIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { usePermissions } from '../../contexts/PermissionContext';

const drawerWidth = 240;

interface SidebarItem {
  text: string;
  icon: React.ReactNode;
  path: string;
  permission: string;
}

interface SidebarSection {
  title: string;
  items: SidebarItem[];
}

const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission } = usePermissions();

  const sections: SidebarSection[] = [
    {
      title: 'MAIN',
      items: [
        { text: 'Dashboard', icon: <DashboardIcon />, path: '/', permission: 'CRM_DASHBOARD_VIEW' },
        { text: 'Leads', icon: <PeopleIcon />, path: '/leads', permission: 'CRM_LEAD_VIEW' },
        { text: 'Pipeline', icon: <PipelineIcon />, path: '/pipeline', permission: 'CRM_PIPELINE_VIEW' },
        { text: 'Tasks', icon: <TaskIcon />, path: '/tasks', permission: 'CORE_TASK_VIEW' },
      ]
    },
    {
      title: 'COMMUNICATION',
      items: [
        { text: 'Hub', icon: <CommIcon />, path: '/communications', permission: 'COMM_HUB_VIEW' },
        { text: 'WhatsApp', icon: <WhatsAppIcon />, path: '/whatsapp', permission: 'COMM_WHATSAPP_SEND' },
      ]
    },
    {
      title: 'AUTOMATION',
      items: [
        { text: 'Workflows', icon: <WorkflowIcon />, path: '/workflows', permission: 'AUTO_WORKFLOW_VIEW' },
      ]
    },
    {
      title: 'INTEGRATIONS',
      items: [
        { text: 'Meta Business', icon: <MetaIcon />, path: '/integrations/meta', permission: 'INT_META_VIEW' },
      ]
    },
    {
      title: 'ADMINISTRATION',
      items: [
        { text: 'Security Center', icon: <SecurityIcon />, path: '/admin/security', permission: 'SECURITY_CENTER_VIEW' },
        { text: 'Super Admin', icon: <SuperAdminIcon />, path: '/super-admin', permission: 'SYS_ADMIN_ACCESS' },
        { text: 'Settings', icon: <SettingsIcon />, path: '/settings', permission: 'ADMIN_ORG_MANAGE' },
      ]
    }
  ];

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        [`& .MuiDrawer-paper`]: { width: drawerWidth, boxSizing: 'border-box' },
      }}
    >
      <Toolbar />
      <Box sx={{ overflow: 'auto', py: 2 }}>
        {sections.map((section) => {
          const visibleItems = section.items.filter(item => hasPermission(item.permission));
          if (visibleItems.length === 0) return null;

          return (
            <React.Fragment key={section.title}>
              <Typography
                variant="overline"
                sx={{ px: 3, mt: 2, mb: 1, display: 'block', color: 'text.secondary', fontWeight: 'bold' }}
              >
                {section.title}
              </Typography>
              <List>
                {visibleItems.map((item) => (
                  <ListItem key={item.text} disablePadding>
                    <ListItemButton
                      onClick={() => navigate(item.path)}
                      selected={location.pathname === item.path}
                    >
                      <ListItemIcon sx={{ color: location.pathname === item.path ? 'primary.main' : 'inherit' }}>
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.text}
                        primaryTypographyProps={{
                          variant: 'body2',
                          fontWeight: location.pathname === item.path ? 'bold' : 'medium'
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
              <Divider sx={{ mx: 2, my: 1 }} />
            </React.Fragment>
          );
        })}
      </Box>
    </Drawer>
  );
};

export default Sidebar;
